/**
 * StudioLayout — premium creator-focused shell.
 *
 * Visually distinct from the enterprise admin/manager Layout. Warm tones,
 * elegant gradients, mobile-first. Sidebar is collapsible; navbar carries
 * a small workspace identity badge.
 */
import { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  ChefHat, BookOpen, DollarSign, Calendar, Sparkles, LogOut, Menu, X,
  ArrowLeft, Package, Camera,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const studioNav = [
  { to: '/studio/home',        label: 'Home',               icon: Sparkles },
  { to: '/studio/ingredients', label: 'Ingredients',        icon: Package },
  { to: '/studio/recipes',     label: 'Recipe Studio',      icon: ChefHat },
  { to: '/studio/pricing',     label: 'Pricing Studio',     icon: DollarSign },
  { to: '/studio/production',  label: 'Production Planner', icon: Calendar },
  { to: '/studio/library',     label: 'Recipe Library',     icon: BookOpen },
];

export default function StudioLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/studio/login'); };

  const NavLink = ({ to, label, Icon, onClick }) => {
    const active = location.pathname === to;
    return (
      <Link to={to} onClick={onClick}
        data-testid={`studio-nav-${to.split('/').pop()}`}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
          active
            ? 'bg-gradient-to-r from-[#E8643E]/15 to-[#F4B860]/10 text-[#7A2A12] shadow-sm'
            : 'text-[#5C4A3D] hover:bg-[#F4ECE0]/60 hover:text-[#7A2A12]'
        }`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
        {label}
      </Link>
    );
  };

  return (
    <div data-testid="studio-layout"
      className="min-h-screen relative"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, #FFF4E6 0%, transparent 35%), ' +
          'radial-gradient(circle at 100% 0%, #F4DCC8 0%, transparent 35%), ' +
          'linear-gradient(180deg, #FFFBF5 0%, #FAF3EA 100%)',
      }}>
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FFFBF5]/80 border-b border-[#F4ECE0]"
        data-testid="studio-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Link to="/studio/home" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E8643E] to-[#F4B860] flex items-center justify-center shadow-md">
              <ChefHat className="w-4 h-4 text-white" strokeWidth={2.25} />
            </div>
            <div className="leading-none">
              <p className="text-[15px] font-bold text-[#3A1F12] tracking-tight">Chef Studio</p>
              <p className="text-[10px] text-[#A0846B] font-medium">by RestroPulse</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {studioNav.map(n => (
              <NavLink key={n.to} to={n.to} label={n.label} Icon={n.icon} />
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden lg:flex flex-col items-end leading-tight">
              <p className="text-[12px] font-semibold text-[#3A1F12]">{user?.name || 'Studio user'}</p>
              <p className="text-[10px] text-[#A0846B]">{user?.phone || 'Workspace'}</p>
            </div>
            <StudioAvatarUploader />
            <Button variant="ghost" size="sm" onClick={handleLogout}
              data-testid="studio-logout-btn"
              className="text-[#5C4A3D] hover:text-[#C45A41] hover:bg-[#FCE7D6]/60 h-8 w-8 p-0">
              <LogOut className="w-4 h-4" />
            </Button>
            <button type="button"
              onClick={() => setMobileOpen(o => !o)}
              data-testid="studio-mobile-toggle"
              className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-[#3A1F12] hover:bg-[#F4ECE0]">
              {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-[#F4ECE0] bg-[#FFFBF5] px-3 py-2 space-y-1"
            data-testid="studio-mobile-nav">
            {studioNav.map(n => (
              <NavLink key={n.to} to={n.to} label={n.label} Icon={n.icon}
                onClick={() => setMobileOpen(false)} />
            ))}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        {children}
      </main>

      <footer className="max-w-7xl mx-auto px-6 py-6 mt-8 text-center text-[10px] text-[#A0846B]">
        Chef Studio · powered by RestroPulse · Phase 1 · Recipe & Pricing for creators
      </footer>
    </div>
  );
}

export function StudioBackButton({ to = '/studio/home' }) {
  return (
    <Link to={to}
      data-testid="studio-back-btn"
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A2A12] hover:text-[#3A1F12] mb-3">
      <ArrowLeft className="w-3 h-3" />
      Back to Studio
    </Link>
  );
}

function StudioAvatarUploader() {
  const { user, getHeaders, refresh } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const initials = (user?.name || 'C').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const avatar = user?.profile_image_b64;

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error('Image too large (max 1.5 MB)'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = () => rej(new Error('Read failed'));
        reader.readAsDataURL(f);
      });
      await axios.put(`${API}/studio/profile`, { profile_image_b64: dataUrl }, { headers: getHeaders() });
      if (refresh) await refresh();
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not upload photo');
    } finally { setUploading(false); }
  };

  return (
    <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
      data-testid="studio-avatar-btn"
      title="Update profile photo"
      className="relative w-9 h-9 rounded-full overflow-hidden ring-1 ring-[#F4ECE0] hover:ring-[#E8643E] transition-all group">
      {avatar ? (
        <img src={avatar} alt={user?.name || 'avatar'} className="w-full h-full object-cover" />
      ) : (
        <span className="w-full h-full bg-gradient-to-br from-[#E8643E] to-[#F4B860] text-white text-[12px] font-bold flex items-center justify-center">{initials}</span>
      )}
      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <Camera className="w-3.5 h-3.5 text-white" />
      </span>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
        onChange={onPick} className="hidden" data-testid="studio-avatar-input" />
    </button>
  );
}

